import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Compass,
  Eye,
  Scale,
  Globe2,
} from "lucide-react";
import AbstractShapes from "@/components/landing/AbstractShapes";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Scientific Integrity",
    text: "Evidence-based publishing rooted in transparent, reproducible methods.",
  },
  {
    icon: Compass,
    title: "Editorial Independence",
    text: "Editors and reviewers operate free of commercial or institutional influence.",
  },
  {
    icon: Eye,
    title: "Research Transparency",
    text: "Open data, declared conflicts, and traceable revisions at every stage.",
  },
  {
    icon: Scale,
    title: "Ethical Publishing",
    text: "Aligned with COPE principles, Helsinki Declaration & ICMJE recommendations.",
  },
  {
    icon: Globe2,
    title: "Global Accessibility",
    text: "Open access by default — research that travels without borders or paywalls.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[var(--sci-bg)] overflow-hidden"
    >
      <AbstractShapes
        variant="constellation"
        tone="ink"
        className="left-0 top-10 w-[380px] h-[380px] opacity-70"
      />
      <AbstractShapes
        variant="sigil"
        tone="ink"
        className="right-[-80px] bottom-0 w-[420px] h-[420px] opacity-40"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="overline text-[var(--sci-teal)] mb-5">
              · 01 / About the Publisher
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
              About <br />
              International <br />
              <em className="not-italic text-[var(--sci-teal)]">Sci Ink Press</em>.
            </h2>
            <div className="mt-8 space-y-5 text-[var(--sci-text)]/80 leading-relaxed">
              <p>
                International Sci Ink Press Ltd is an academic publishing company
                established in <span className="text-[var(--sci-navy)] font-medium">Bulgaria in 2018</span> by{" "}
                <Link
                  to="/editor/ivan-inkov"
                  data-testid="about-ceo-link"
                  className="text-[var(--sci-text)]/80 hover:text-[var(--sci-teal)] transition-colors"
                >
                  Dr Ivan Inkov
                </Link>
                , who serves as <span className="text-[var(--sci-navy)] font-medium">CEO &amp; Founder</span> of the publisher and as <span className="text-[var(--sci-navy)] font-medium">Editor-in-Chief</span> of both IJMRCR and IJSM.
              </p>
              <p>
                Our mission is to support the dissemination of high-quality medical
                and surgical research through transparent editorial processes,
                ethical publishing standards, and international scientific
                collaboration.
              </p>
              <p
                data-testid="about-coi-firewall"
                className="text-sm text-[var(--sci-text-muted)] leading-relaxed border border-dashed border-[var(--sci-border)] bg-white/60 p-4"
              >
                <span className="overline text-[var(--sci-teal)] block mb-1.5">
                  Editorial Firewall · COPE-aligned
                </span>
                To preserve editorial independence, any manuscript authored or
                co-authored by Dr Inkov is reassigned to an independent Deputy
                Editor and undergoes blind external peer review. Conflicts of
                interest are publicly declared on every accepted article.
              </p>
              <p className="text-[var(--sci-navy)] font-serif-display text-2xl md:text-3xl leading-snug pt-4 border-l-2 border-[var(--sci-teal)] pl-5">
                "We believe scientific knowledge should be accessible, discoverable,
                and impactful."
              </p>
            </div>
          </motion.div>

          {/* Bento grid */}
          <div className="lg:col-span-7 grid grid-cols-6 gap-4 md:gap-5">
            {/* Hero cell - image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="col-span-6 md:col-span-6 relative aspect-[16/9] overflow-hidden border border-[var(--sci-border)]"
            >
              <img
                src="https://images.pexels.com/photos/15831822/pexels-photo-15831822.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Researcher in a modern medical laboratory"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--sci-navy)]/85 via-[var(--sci-navy)]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <div className="overline text-white/70">Core Values</div>
                <div className="font-serif-display text-2xl md:text-3xl mt-2">
                  Five principles, one editorial standard.
                </div>
              </div>
            </motion.div>

            {VALUES.map((v, i) => {
              const Icon = v.icon;
              // Variable spans for asymmetry
              const span =
                i === 0
                  ? "col-span-6 md:col-span-3"
                  : i === 1
                    ? "col-span-6 md:col-span-3"
                    : i === 2
                      ? "col-span-6 md:col-span-2"
                      : i === 3
                        ? "col-span-6 md:col-span-2"
                        : "col-span-6 md:col-span-2";
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.55, delay: 0.06 * i }}
                  data-testid={`about-value-${i}`}
                  className={`${span} bg-white border border-[var(--sci-border)] card-lift p-6 md:p-7`}
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-[var(--sci-navy)]/15 text-[var(--sci-teal)]">
                      <Icon size={18} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-serif-display text-lg text-[var(--sci-navy)] leading-tight">
                        {v.title}
                      </div>
                      <p className="text-sm text-[var(--sci-text-muted)] mt-2 leading-relaxed">
                        {v.text}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
